import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { SocialIcon } from "@/components/ui/LinkIcon";
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  GripVertical,
  Save,
  Image as ImageIcon,
  ChevronRight,
  Settings,
  X,
  Camera,
  Instagram,
  Youtube,
  ExternalLink,
  LogOut
} from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a4b1710a-e8aa-40f2-90ed-6aa15c96d321/artifacts/w14lowsf_Disen%CC%83o%20sin%20ti%CC%81tulo.png";

// Use relative URL for API calls so it works on any domain
const getApiUrl = () => {
  if (window.location.hostname.includes('preview.emergentagent.com')) {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  return '';
};

const iconOptions = [
  { value: "globe", label: "Web/General" },
  { value: "onlyfans", label: "OnlyFans" },
  { value: "playboy", label: "Playboy" },
  { value: "mym", label: "MYM" },
  { value: "instagram", label: "Instagram" },
  { value: "twitter", label: "Twitter/X" },
  { value: "tiktok", label: "TikTok" },
  { value: "telegram", label: "Telegram" },
  { value: "youtube", label: "YouTube" },
  { value: "lock", label: "Privado/VIP" },
  { value: "mail", label: "Email" },
  { value: "shopping-bag", label: "Tienda" },
];

const sizeOptions = [
  { value: "large", label: "Grande" },
  { value: "medium", label: "Mediano" },
  { value: "small", label: "Pequeño" },
];

const colorPresets = [
  { bg: "#1a1a1a", text: "#ffffff" },
  { bg: "#dc2626", text: "#ffffff" },
  { bg: "#ea580c", text: "#ffffff" },
  { bg: "#ca8a04", text: "#000000" },
  { bg: "#16a34a", text: "#ffffff" },
  { bg: "#0891b2", text: "#ffffff" },
  { bg: "#2563eb", text: "#ffffff" },
  { bg: "#7c3aed", text: "#ffffff" },
  { bg: "#db2777", text: "#ffffff" },
];

const socialPlatforms = [
  { key: "onlyfans", label: "OnlyFans", color: "#00AFF0" },
  { key: "playboy", label: "Playboy", color: "#000000" },
  { key: "mym", label: "MYM", color: "#000000" },
  { key: "instagram", label: "Instagram", color: "#E4405F" },
  { key: "twitter", label: "Twitter", color: "#1DA1F2" },
  { key: "tiktok", label: "TikTok", color: "#000000" },
  { key: "telegram", label: "Telegram", color: "#0088cc" },
  { key: "youtube", label: "YouTube", color: "#FF0000" },
];

const ModeloEditarPerfil = () => {
  const navigate = useNavigate();
  const isCreating = window.location.pathname.includes("crear");
  const fileInputRef = useRef(null);
  const linkImageRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    bio: "",
    avatar: "",
    coverImage: "",
    links: [],
    socialLinks: {},
    galleryImages: []
  });

  // UI State
  const [editingLink, setEditingLink] = useState(null);

  // Section toggles
  const [sections, setSections] = useState({
    bio: true,
    featuredLinks: true,
    gallery: false
  });

  // Check auth and load profile
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionToken = params.get('session');
    if (sessionToken) {
      localStorage.setItem("jglinks_session", sessionToken);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    checkAuthAndLoadProfile();
  }, []);

  const checkAuthAndLoadProfile = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/auth/me`, {
        credentials: "include",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("jglinks_session")}`
        }
      });

      if (!response.ok) throw new Error("Not authenticated");
      
      const userData = await response.json();
      setUser(userData);

      if (!userData.username && isCreating) {
        navigate("/modelo/completar-perfil");
        return;
      }

      // Load existing profile if editing
      if (!isCreating) {
        const profileResponse = await fetch(`${getApiUrl()}/api/model/profile`, {
          credentials: "include",
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("jglinks_session")}`
          }
        });

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          if (profileData) {
            setFormData({
              name: profileData.name || userData.name || "",
              username: profileData.username || userData.username || "",
              bio: profileData.bio || "",
              avatar: profileData.avatar || userData.picture || "",
              coverImage: profileData.coverImage || "",
              links: profileData.links || [],
              socialLinks: profileData.socialLinks || {},
              galleryImages: profileData.galleryImages || []
            });
          } else {
            navigate("/modelo/crear-perfil");
          }
        }
      } else {
        // Set defaults for new profile
        setFormData(prev => ({
          ...prev,
          name: userData.name || "",
          username: userData.username || "",
          avatar: userData.picture || ""
        }));
      }
    } catch (error) {
      console.error("Auth error:", error);
      navigate("/modelo/login");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle social link changes
  const handleSocialLinkChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [platform]: value }
    }));
  };

  // Compress image before upload
  const compressImage = (file, maxWidth = 800) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compress to JPEG with 80% quality
          const compressed = canvas.toDataURL('image/jpeg', 0.8);
          resolve(compressed);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle image upload with compression
  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("La imagen no debe superar los 10MB");
      return;
    }

    try {
      toast.loading("Procesando imagen...", { id: 'image-upload' });
      const compressed = await compressImage(file, field === 'coverImage' ? 1200 : 600);
      setFormData(prev => ({ ...prev, [field]: compressed }));
      toast.success("Imagen cargada", { id: 'image-upload' });
    } catch (error) {
      toast.error("Error al procesar la imagen", { id: 'image-upload' });
    }
  };

  // Handle gallery image upload
  const handleGalleryUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Cada imagen no debe superar 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          galleryImages: [...prev.galleryImages, {
            id: Date.now().toString() + Math.random(),
            url: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.filter(img => img.id !== imageId)
    }));
  };

  // Handle link changes
  const handleLinkChange = (field, value) => {
    if (editingLink === null) return;
    const newLinks = [...formData.links];
    newLinks[editingLink] = { ...newLinks[editingLink], [field]: value };
    setFormData(prev => ({ ...prev, links: newLinks }));
  };

  const handleLinkImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || editingLink === null) return;

    const reader = new FileReader();
    reader.onload = () => {
      handleLinkChange('image', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const addLink = () => {
    const newLink = {
      id: Date.now().toString(),
      title: "Nuevo Enlace",
      url: "",
      icon: "globe",
      image: "",
      size: "medium",
      bgColor: "#1a1a1a",
      textColor: "#ffffff",
      featured: formData.links.length === 0
    };
    setFormData(prev => ({ ...prev, links: [...prev.links, newLink] }));
    setEditingLink(formData.links.length);
  };

  const removeLink = (index) => {
    const newLinks = formData.links.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, links: newLinks }));
    setEditingLink(null);
  };

  const toggleSection = (section) => {
    setSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    if (!formData.username.trim()) {
      toast.error("El username es requerido");
      return;
    }

    setIsSubmitting(true);

    try {
      const cleanedSocialLinks = Object.fromEntries(
        Object.entries(formData.socialLinks).filter(([_, v]) => v && v.trim())
      );

      const dataToSave = {
        ...formData,
        socialLinks: cleanedSocialLinks,
        coverImage: formData.coverImage || formData.avatar
      };

      const url = `${getApiUrl()}/api/model/profile`;
      const method = isCreating ? "POST" : "PUT";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("jglinks_session")}`
        },
        credentials: "include",
        body: JSON.stringify(dataToSave)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Error al guardar");
      }

      toast.success(isCreating ? "¡Perfil creado!" : "Perfil actualizado");
      navigate("/modelo/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${getApiUrl()}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "Authorization": `Bearer ${localStorage.getItem("jglinks_session")}` }
      });
    } catch (e) {}
    localStorage.removeItem("jglinks_session");
    navigate("/");
  };

  const currentLink = editingLink !== null ? formData.links[editingLink] : null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Top Bar */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-[#0a0a0a] border-b border-white/10 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate("/modelo/dashboard")}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Link to="/">
            <img src={LOGO_URL} alt="JG Models" className="h-8" />
          </Link>
        </div>
        
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full text-sm">
          <span className="text-white/60">Tu Bio Link</span>
          <span className="font-medium">jglinks.com/{formData.username || 'username'}</span>
        </div>

        <div className="flex items-center gap-3">
          {formData.username && (
            <Link to={`/${formData.username}`} target="_blank">
              <Button variant="ghost" className="text-white/60 hover:text-white">
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver perfil
              </Button>
            </Link>
          )}
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-white text-black hover:bg-white/90 rounded-full px-6"
          >
            {isSubmitting ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row pt-14 min-h-screen">
        {/* Left Side - Live Preview (Hidden on mobile) */}
        <div className="hidden lg:flex lg:w-1/2 border-r border-white/10 items-start justify-center p-8 bg-[#111]">
          <div className="w-full max-w-sm">
            {/* Phone Frame Preview */}
            <div className="bg-black rounded-[2.5rem] p-2 shadow-2xl">
              <div className="bg-[#0a0a0a] rounded-[2rem] overflow-hidden">
                <div className="relative">
                  {/* Cover/Avatar Section */}
                  <div className="relative h-72 bg-gradient-to-b from-white/5 to-transparent">
                    {formData.avatar ? (
                      <img src={formData.avatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                  </div>

                  {/* Profile Info */}
                  <div className="relative -mt-20 px-6 pb-6 text-center">
                    <h2 className="text-2xl font-bold text-white mb-1">
                      {formData.name || "Nombre"}
                    </h2>
                    
                    <button 
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm text-white/60 hover:text-white/80 mb-1"
                    >
                      Cambiar Foto de Perfil
                    </button>
                    
                    <p className="text-white/50 text-sm mb-4">
                      @{formData.username || "username"}
                    </p>

                    {/* Social Icons */}
                    <div className="flex items-center justify-center gap-2 mb-4">
                      {Object.entries(formData.socialLinks).filter(([_, v]) => v).map(([platform]) => (
                        <div key={platform} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                          <SocialIcon platform={platform} className="w-4 h-4" />
                        </div>
                      ))}
                      {Object.keys(formData.socialLinks).filter(k => formData.socialLinks[k]).length === 0 && (
                        <>
                          <div className="w-9 h-9 rounded-full bg-pink-500/80 flex items-center justify-center">
                            <Plus className="w-4 h-4" />
                          </div>
                          <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-yellow-500 via-pink-500 to-purple-500 flex items-center justify-center">
                            <Instagram className="w-4 h-4" />
                          </div>
                          <div className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center">
                            <Youtube className="w-4 h-4" />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Bio Preview */}
                    {sections.bio && formData.bio && (
                      <p className="text-white/60 text-sm mb-6">{formData.bio}</p>
                    )}

                    {/* Links Preview */}
                    {sections.featuredLinks && formData.links.length > 0 && (
                      <div className="space-y-3">
                        {formData.links.map((link, idx) => (
                          <div 
                            key={link.id}
                            onClick={() => setEditingLink(idx)}
                            className={`rounded-xl overflow-hidden cursor-pointer transition-all ${
                              editingLink === idx ? 'ring-2 ring-primary' : ''
                            }`}
                            style={{ backgroundColor: link.bgColor || '#1a1a1a' }}
                          >
                            {link.image && link.size !== 'small' ? (
                              <div className={`relative ${link.size === 'large' ? 'aspect-video' : 'aspect-[3/1]'}`}>
                                <img src={link.image} alt="" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                                <div className="absolute bottom-3 left-3 right-3">
                                  <span className="font-medium" style={{ color: link.textColor }}>
                                    {link.title}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="px-4 py-3 text-center">
                                <span className="font-medium" style={{ color: link.textColor }}>
                                  {link.title}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Editor Panel */}
        <div className="w-full lg:w-1/2 overflow-y-auto" style={{ height: 'calc(100vh - 56px)' }}>
          <div className="p-4 md:p-6 space-y-4">
            {/* Hidden file inputs */}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'avatar')} className="hidden" />
            <input id="coverInput" type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'coverImage')} className="hidden" />
            <input ref={linkImageRef} type="file" accept="image/*" onChange={handleLinkImageUpload} className="hidden" />
            <input ref={galleryInputRef} type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" />

            {/* Profile Images Section */}
            <div className="bg-white/5 rounded-2xl p-4 space-y-4">
              <div className="flex items-center gap-3">
                <Camera className="w-4 h-4 text-white/50" />
                <span className="font-medium flex-1">Imágenes del Perfil</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 pl-7">
                {/* Avatar Upload */}
                <div>
                  <Label className="text-xs text-white/50 mb-2 block">Foto de Perfil</Label>
                  <div className="relative">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full aspect-square rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors overflow-hidden relative group"
                    >
                      {formData.avatar ? (
                        <>
                          <img src={formData.avatar} alt="Avatar" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Camera className="w-6 h-6 text-white" />
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-white/40">
                          <Camera className="w-8 h-8 mb-2" />
                          <span className="text-xs">Subir foto</span>
                        </div>
                      )}
                    </button>
                    {formData.avatar && (
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, avatar: "" }))}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                        title="Eliminar foto"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Cover Upload */}
                <div>
                  <Label className="text-xs text-white/50 mb-2 block">Foto Banner</Label>
                  <div className="relative">
                    <button
                      onClick={() => document.getElementById('coverInput')?.click()}
                      className="w-full aspect-square rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors overflow-hidden relative group"
                    >
                      {formData.coverImage ? (
                        <>
                          <img src={formData.coverImage} alt="Cover" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Camera className="w-6 h-6 text-white" />
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-white/40">
                          <ImageIcon className="w-8 h-8 mb-2" />
                          <span className="text-xs">Subir banner</span>
                        </div>
                      )}
                    </button>
                    {formData.coverImage && (
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, coverImage: "" }))}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                        title="Eliminar banner"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Basic Info Section */}
            <div className="bg-white/5 rounded-2xl p-4 space-y-4">
              <div className="flex items-center gap-3">
                <GripVertical className="w-4 h-4 text-white/30" />
                <span className="font-medium flex-1">Información Básica</span>
              </div>
              
              <div className="space-y-3 pl-7">
                <div>
                  <Label className="text-xs text-white/50">Nombre</Label>
                  <Input name="name" value={formData.name} onChange={handleChange} placeholder="Tu nombre artístico" className="mt-1 bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-xs text-white/50">Username</Label>
                  <Input name="username" value={formData.username} onChange={handleChange} placeholder="username" className="mt-1 bg-white/5 border-white/10 text-white" />
                </div>
              </div>
            </div>

            {/* Bio Section */}
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <GripVertical className="w-4 h-4 text-white/30" />
                <span className="font-medium flex-1">Bio</span>
                <Switch checked={sections.bio} onCheckedChange={() => toggleSection('bio')} />
              </div>
              
              {sections.bio && (
                <div className="mt-4 pl-7">
                  <Textarea name="bio" value={formData.bio} onChange={handleChange} placeholder="Añade una bio a tu perfil" className="bg-white/5 border-white/10 text-white resize-none" rows={3} />
                </div>
              )}
            </div>

            {/* Social Links Section */}
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-3 mb-4">
                <GripVertical className="w-4 h-4 text-white/30" />
                <span className="font-medium flex-1">Redes Sociales</span>
              </div>
              
              <div className="space-y-3 pl-7">
                {socialPlatforms.map(platform => (
                  <div key={platform.key} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: platform.color }}>
                      <SocialIcon platform={platform.key} className="w-4 h-4 text-white" />
                    </div>
                    <Input
                      value={formData.socialLinks[platform.key] || ""}
                      onChange={(e) => handleSocialLinkChange(platform.key, e.target.value)}
                      placeholder={`URL de ${platform.label}`}
                      className="flex-1 bg-white/5 border-white/10 text-white text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Featured Links Section */}
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-3 mb-4">
                <GripVertical className="w-4 h-4 text-white/30" />
                <span className="font-medium flex-1">Enlaces Destacados</span>
                <Switch checked={sections.featuredLinks} onCheckedChange={() => toggleSection('featuredLinks')} />
              </div>
              
              {sections.featuredLinks && (
                <div className="space-y-3 pl-7">
                  {formData.links.map((link, idx) => (
                    <div 
                      key={link.id}
                      onClick={() => setEditingLink(idx)}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                        editingLink === idx ? 'bg-primary/20 border border-primary' : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <GripVertical className="w-4 h-4 text-white/30" />
                      {link.image ? (
                        <img src={link.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                          <SocialIcon platform={link.icon} className="w-5 h-5 text-white/50" />
                        </div>
                      )}
                      <span className="flex-1 font-medium truncate">{link.title}</span>
                      <button onClick={(e) => { e.stopPropagation(); removeLink(idx); }} className="p-1 hover:bg-white/10 rounded">
                        <Trash2 className="w-4 h-4 text-white/50" />
                      </button>
                    </div>
                  ))}

                  <button onClick={addLink} className="w-full py-3 rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors flex items-center justify-center gap-2 text-white/60 hover:text-white/80">
                    <Plus className="w-4 h-4" />
                    <span>Nuevo Enlace</span>
                  </button>
                </div>
              )}
            </div>

            {/* Gallery Section */}
            <div className="bg-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-3 mb-4">
                <GripVertical className="w-4 h-4 text-white/30" />
                <span className="font-medium flex-1">Galería de Fotos</span>
                <Switch checked={sections.gallery} onCheckedChange={() => toggleSection('gallery')} />
              </div>
              
              {sections.gallery && (
                <div className="pl-7 space-y-4">
                  {formData.galleryImages.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {formData.galleryImages.map((img) => (
                        <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden group">
                          <img src={img.url} alt="" className="w-full h-full object-cover" />
                          <button onClick={() => removeGalleryImage(img.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <button onClick={() => galleryInputRef.current?.click()} className="w-full py-4 rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors flex flex-col items-center justify-center gap-2 text-white/60 hover:text-white/80">
                    <Camera className="w-6 h-6" />
                    <span className="text-sm">Subir fotos a la galería</span>
                  </button>
                  
                  <p className="text-xs text-white/40 text-center">{formData.galleryImages.length} fotos en la galería</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Link Editor Panel */}
        {editingLink !== null && currentLink && (
          <div className="fixed right-0 top-14 bottom-0 w-96 bg-[#0f0f0f] border-l border-white/10 overflow-y-auto z-40">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-lg">Editar Enlace</h3>
                <button onClick={() => setEditingLink(null)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Link Preview */}
              <div className="rounded-xl overflow-hidden mb-6" style={{ backgroundColor: currentLink.bgColor }}>
                {currentLink.image ? (
                  <div className="relative aspect-video">
                    <img src={currentLink.image} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-3 left-3">
                      <span className="font-medium" style={{ color: currentLink.textColor }}>{currentLink.title}</span>
                    </div>
                  </div>
                ) : (
                  <div className="px-6 py-4 text-center">
                    <span className="font-medium" style={{ color: currentLink.textColor }}>{currentLink.title}</span>
                  </div>
                )}
              </div>

              {/* Size Options */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-3 block">Tamaño del enlace</Label>
                <div className="grid grid-cols-3 gap-2">
                  {sizeOptions.map(size => (
                    <button key={size.value} onClick={() => handleLinkChange('size', size.value)} className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${currentLink.size === size.value ? 'bg-primary text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}>
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Image Upload */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-3 block">Imagen del enlace</Label>
                <button onClick={() => linkImageRef.current?.click()} className="w-full py-3 rounded-xl border-2 border-dashed border-white/20 hover:border-white/40 transition-colors flex items-center justify-center gap-2 text-white/60 hover:text-white/80">
                  <Camera className="w-4 h-4" />
                  <span>{currentLink.image ? 'Cambiar imagen' : 'Subir imagen'}</span>
                </button>
              </div>

              {/* URL */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-2 block">URL del enlace</Label>
                <Input value={currentLink.url} onChange={(e) => handleLinkChange('url', e.target.value)} placeholder="https://..." className="bg-white/5 border-white/10 text-white" />
              </div>

              {/* Title */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-2 block">Título</Label>
                <Input value={currentLink.title} onChange={(e) => handleLinkChange('title', e.target.value)} placeholder="Título del enlace" className="bg-white/5 border-white/10 text-white" />
              </div>

              {/* Color Presets */}
              <div className="mb-6">
                <Label className="text-xs text-white/50 mb-3 block">Personalizar Color</Label>
                <div className="flex gap-2 flex-wrap">
                  {colorPresets.map((color, idx) => (
                    <button key={idx} onClick={() => { handleLinkChange('bgColor', color.bg); handleLinkChange('textColor', color.text); }} className={`w-8 h-8 rounded-full border-2 transition-all ${currentLink.bgColor === color.bg ? 'border-white scale-110' : 'border-transparent hover:scale-105'}`} style={{ backgroundColor: color.bg }} />
                  ))}
                </div>
              </div>

              {/* Custom Colors */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div>
                  <Label className="text-xs text-white/50 mb-2 block">Color Fondo</Label>
                  <div className="flex gap-2">
                    <input type="color" value={currentLink.bgColor} onChange={(e) => handleLinkChange('bgColor', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                    <Input value={currentLink.bgColor} onChange={(e) => handleLinkChange('bgColor', e.target.value)} className="flex-1 bg-white/5 border-white/10 text-white text-xs" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-white/50 mb-2 block">Color Texto</Label>
                  <div className="flex gap-2">
                    <input type="color" value={currentLink.textColor} onChange={(e) => handleLinkChange('textColor', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                    <Input value={currentLink.textColor} onChange={(e) => handleLinkChange('textColor', e.target.value)} className="flex-1 bg-white/5 border-white/10 text-white text-xs" />
                  </div>
                </div>
              </div>

              {/* Delete Link */}
              <button onClick={() => removeLink(editingLink)} className="w-full py-3 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2">
                <Trash2 className="w-4 h-4" />
                <span>Eliminar Enlace</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModeloEditarPerfil;
